<!--  -->
<script setup>

// 辅助函数 - 需要在组件函数之前定义
const throttle = (func, limit) => {
    let lastFunc
    let lastRan
    return function () {
        const context = this
        const args = arguments
        if (!lastRan) {
            func.apply(context, args)
            lastRan = Date.now()
        } else {
            clearTimeout(lastFunc)
            lastFunc = setTimeout(function () {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(context, args)
                    lastRan = Date.now()
                }
            }, limit - (Date.now() - lastRan))
        }
    }
}

const easeInOutQuad = (t) => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

const props = defineProps({
    quickjump: {
        type: Array,
        required: true,
        default: () => [
            { id: 1, name: '产品优势', target: 'Heziyun-advantage' },
            { id: 2, name: '产品功能', target: 'Heziyun-function' },
            { id: 3, name: '应用场景', target: 'Heziyun-applicationScenarios' },
            { id: 4, name: '客户之声', target: 'Heziyun-custome' },
            { id: 5, name: '产品对比', target: 'Heziyun-characteristics' },
            { id: 6, name: '快速入门', target: 'Heziyun-quickStart' }
        ]
    }
})

// 响应式数据
const activeSection = ref('')
const isScrolling = ref(false)
const observer = ref(null)
const resizeObserver = ref(null)
const scrollTimeout = ref(null)
const styleElement = ref(null)
const underlineRef = ref(null)

// 计算属性
const processedQuickJump = computed(() => {
    return props.quickjump.map(item => ({
        ...item,
        target: item.target.startsWith('.') ? item.target.slice(1) : item.target
    }))
})


const cleanup = () => {
    if (observer.value) {
        observer.value.disconnect()
        observer.value = null
    }

    if (resizeObserver.value) {
        resizeObserver.value.disconnect()
        resizeObserver.value = null
    }

    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value)
        scrollTimeout.value = null
    }

    if (styleElement.value) {
        styleElement.value.remove()
        styleElement.value = null
    }
}

const positionUnderline = () => {
    nextTick(() => {
        if (!underlineRef.value) return

        const activeItem = document.querySelector(`.Heziyun-item[data-target="${activeSection.value}"]`)
        if (activeItem && underlineRef.value) {
            const underline = underlineRef.value
            underline.style.width = `${activeItem.offsetWidth}px`
            underline.style.left = `${activeItem.offsetLeft}px`
        }
    })
}

const initIntersectionObserver = () => {
    if (!window.IntersectionObserver) {
        fallbackScrollHandler()
        return
    }

    const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.3
    }

    observer.value = new IntersectionObserver((entries) => {
        if (isScrolling.value) return

        requestAnimationFrame(() => {
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i]
                if (entry.isIntersecting) {
                    const classList = entry.target.classList
                    for (let j = 0; j < processedQuickJump.value.length; j++) {
                        const item = processedQuickJump.value[j]
                        if (classList.contains(item.target)) {
                            activeSection.value = item.target
                            break
                        }
                    }
                    break
                }
            }
        })
    }, options)

    const targets = document.querySelectorAll(
        processedQuickJump.value.map(item => `.${item.target}`).join(',')
    )
    targets.forEach(target => {
        observer.value.observe(target)
    })
}

const initResizeObserver = () => {
    if (window.ResizeObserver) {
        resizeObserver.value = new ResizeObserver(() => {
            positionUnderline()
        })
        const element = document.querySelector('.Heziyun-quickJump')
        if (element) {
            resizeObserver.value.observe(element)
        }
    }
}

const handleScrollFallback = throttle(() => {
    if (isScrolling.value) return

    const scrollPosition = window.scrollY + 150
    let closestSection = null
    let minDistance = Infinity

    processedQuickJump.value.forEach(item => {
        const target = document.querySelector(`.${item.target}`)
        if (target) {
            const distance = Math.abs(target.offsetTop - scrollPosition)
            if (distance < minDistance) {
                minDistance = distance
                closestSection = item.target
            }
        }
    })

    if (closestSection && closestSection !== activeSection.value) {
        activeSection.value = closestSection
    }
}, 100)

const fallbackScrollHandler = () => {
    window.addEventListener('scroll', handleScrollFallback)
}

const smoothScrollPolyfill = (targetPosition) => {
    const start = window.scrollY
    const distance = targetPosition - start
    const duration = 500
    let startTime = null

    const animateScroll = (timestamp) => {
        if (!startTime) startTime = timestamp
        const elapsed = timestamp - startTime
        const progress = Math.min(elapsed / duration, 1)
        const ease = easeInOutQuad(progress)

        window.scrollTo(0, start + distance * ease)

        if (progress < 1) {
            requestAnimationFrame(animateScroll)
        }
    }

    requestAnimationFrame(animateScroll)
}

const scrollToSection = (targetClass) => {
    const target = document.querySelector(`.${targetClass}`)
    if (!target) return

    isScrolling.value = true
    activeSection.value = targetClass

    const targetPosition = Math.max(0, target.offsetTop - 120)

    if ('scrollBehavior' in document.documentElement.style) {
        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
        })
    } else {
        smoothScrollPolyfill(targetPosition)
    }

    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value)
    }

    scrollTimeout.value = setTimeout(() => {
        isScrolling.value = false
        scrollTimeout.value = null
    }, 1000)
}

// 监听 activeSection 变化，自动更新下划线位置
watch(activeSection, () => {
    positionUnderline()
})

// 生命周期
onMounted(() => {
    nextTick(() => {
        initIntersectionObserver()
        initResizeObserver()
        positionUnderline()
    })
})

onBeforeUnmount(() => {
    cleanup()
})
</script>
<template>
    <div class="Heziyun-quickJump">
        <div class="Heziyun-defaultWidth">
            <div class="Heziyun-item" v-for="item in processedQuickJump" :key="item.id" :data-target="item.target"
                @click="scrollToSection(item.target)" :class="{ 'active': activeSection === item.target }">
                {{ item.name }}
            </div>
            <div class="Heziyun-underline" ref="underlineRef"></div>
        </div>
    </div>
</template>


<style scoped>
.Heziyun-quickJump {
    background-color: #fff;
    position: sticky;
    top: 3.6rem;
    z-index: 2;
    box-shadow: 0 4px 10px rgba(20, 100, 255, 0.1);
    will-change: transform;
}

.Heziyun-quickJump .Heziyun-defaultWidth {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem;
    position: relative;
}

.Heziyun-quickJump .Heziyun-defaultWidth .Heziyun-item {
    padding: 0.5rem;
    cursor: pointer;
    font-size: 0.8rem;
    will-change: transform;
}

.Heziyun-quickJump .Heziyun-defaultWidth .Heziyun-item:hover {
    color: #216df0;
}

.Heziyun-quickJump .Heziyun-defaultWidth .active {
    color: #216df0;
    font-weight: bold;
}

.Heziyun-quickJump .Heziyun-defaultWidth .Heziyun-underline {
    position: absolute;
    bottom: 0;
    height: 1.5px;
    background-color: #216df0;
    transition: left 0.3s ease;
    transform-origin: left center;
    width: 100%;
    width: 0;
}

@media (max-width: 768px) {
    .Heziyun-quickJump {
        top: 15vw;
    }

    .Heziyun-quickJump .Heziyun-defaultWidth {
        gap: 3vw;
        padding: 2vw 5vw;
        flex-wrap: nowrap;
        overflow-x: auto;
    }

    .Heziyun-quickJump .Heziyun-defaultWidth::-webkit-scrollbar {
        display: none;
    }

    .Heziyun-quickJump .Heziyun-defaultWidth .Heziyun-item {
        padding: 1.5vw 2vw;
        font-size: 3.5vw;
        white-space: nowrap;
    }
}
</style>